import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, AlertTriangle } from 'lucide-react';
import { SignalAccountStatus } from '@/types/signal';
import { CTraderSignalStatusBadge } from './CTraderSignalStatusBadge';
import { formatDateTime, truncateId } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface CTraderSignalTableProps {
  signals: SignalAccountStatus[];
  isLoading: boolean;
  onViewDetails: (signal: SignalAccountStatus) => void;
}

export const CTraderSignalTable = ({ 
  signals, 
  isLoading, 
  onViewDetails 
}: CTraderSignalTableProps) => {
  if (isLoading) {
    return (
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Signal ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Symbol</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Volume</TableHead>
              <TableHead>Order ID</TableHead>
              <TableHead>Response Time</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-8 w-16" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (signals.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <h3 className="text-lg font-semibold mb-2">No signals found</h3>
        <p className="text-muted-foreground">
          No CTrader signals match your current filters.
        </p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Signal ID</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Symbol</TableHead>
            <TableHead>Account</TableHead>
            <TableHead>Volume</TableHead>
            <TableHead>Order ID</TableHead>
            <TableHead>Response Time</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {signals.map((signal) => (
            <TableRow key={signal.id}>
              <TableCell className="font-mono text-sm">
                {truncateId(signal.id)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <CTraderSignalStatusBadge status={signal.status} />
                  {signal.error_message && (
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              </TableCell>
              <TableCell className="font-semibold">
                {signal.symbol || 'N/A'}
              </TableCell>
              <TableCell className="font-mono text-sm">
                {signal.accountid_trading}
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  <div className="font-semibold">
                    {signal.volume_sent?.toLocaleString()}
                  </div>
                  {signal.original_volume !== signal.volume_sent && (
                    <div className="text-xs text-muted-foreground">
                      orig: {signal.original_volume?.toLocaleString()}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell className="font-mono text-sm">
                {signal.order_id || 'N/A'}
              </TableCell>
              <TableCell className="text-sm">
                {signal.response_time ? formatDateTime(signal.response_time) : 'N/A'}
              </TableCell>
              <TableCell>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewDetails(signal)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};